import { isDefined } from "@clipboard-health/util-ts";
import { CircularProgress, Stack, ThemeProvider } from "@mui/material";
// eslint-disable-next-line no-restricted-imports
import { OnboardingRouterPath } from "@src/app/routing/constant/onboardingRoute";
import { OrientationScheduledConfirmation } from "@src/appV2/ClipboardOrientation/components/OrientationScheduledConfirmation";
import { type OrientationRescheduledLocationState } from "@src/appV2/ClipboardOrientation/types";
import { isWorkerTimezoneMatchingDevice } from "@src/appV2/ClipboardOrientation/utils";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { ACCOUNTS_BASE_PATH } from "@src/appV2/redesign/Accounts/paths";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { getSafeAreaPaddings, getTheme } from "@src/appV2/redesign/system/ui-theme";
import { useSkillsAssessmentFeatureFlags } from "@src/appV2/SkillsAssessment/api/useSkillsAssessmentFeatureFlags";
import { useSkillsAssessments } from "@src/appV2/SkillsAssessment/api/useSkillsAssessments";
import { SKILLS_ASSESSMENT_BASE_PATH } from "@src/appV2/SkillsAssessment/paths";
import {
  type AssessmentEvent,
  type SkillsAssessmentStatus,
} from "@src/appV2/SkillsAssessment/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { Redirect, useHistory, useLocation } from "react-router-dom";

interface OrientationRescheduledProps {
  // User can land using a deep link in one of parent routes
  // We need to know if user is on signup or not to navigate to the correct page
  isSignup?: boolean;
}

export function OrientationRescheduled(props: OrientationRescheduledProps) {
  const { isSignup = false } = props;

  const worker = useDefinedWorker();
  const history = useHistory();

  useLogEffect(APP_V2_USER_EVENTS.CLIPBOARD_ORIENTATION_RESCHEDULED_PAGE_VIEWED, {
    isSignup,
  });

  const { skillsAssessmentConfig } = useSkillsAssessmentFeatureFlags();
  const {
    data: assessments,
    isLoading,
    isSuccess,
  } = useSkillsAssessments(
    {
      workerId: worker.userId,
      qualificationNames: skillsAssessmentConfig.availableQualificationNames,
    },
    {
      staleTime: 0,
      cacheTime: 0,
    }
  );

  const latestScheduledEventId =
    assessments?.data?.[0]?.relationships.latestScheduledEvent.data?.id;
  const assessmentStatusId = assessments?.data?.[0]?.relationships.assessmentStatus.data?.id;

  const assessmentStatus = assessments?.included.find(
    (included) => included.type === "assessment-status" && included.id === assessmentStatusId
  ) as SkillsAssessmentStatus | undefined;
  const latestScheduledEvent = assessments?.included.find(
    (included) => included.type === "assessment-event" && included.id === latestScheduledEventId
  ) as AssessmentEvent | undefined;

  const scheduledFor = latestScheduledEvent?.attributes?.scheduledFor;

  const showTimezone = !isWorkerTimezoneMatchingDevice(worker.tmz);

  const location = useLocation<OrientationRescheduledLocationState>();

  // If user directly lands on rescheduled page, we redirect to skills assessment page
  const isNavigatedFromLandingPage =
    location.state?.origin === "CLIPBOARD_ORIENTATION_LANDING_PAGE";
  if (!isNavigatedFromLandingPage) {
    if (isSignup) {
      return <Redirect to={OnboardingRouterPath.ONBOARDING_INFO1} />;
    }

    return <Redirect to={SKILLS_ASSESSMENT_BASE_PATH} />;
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper
        sx={(theme) => ({
          ...getSafeAreaPaddings({
            top: true,
            bottom: true,
            fallbackBottom: theme.spacing(4),
          }),
          background: "#fff",
        })}
      >
        <Stack p={5} height="100%">
          <IconButton
            iconType="chevron-left"
            size="medium"
            variant="outlined"
            aria-label="Back"
            onClick={() => {
              history.goBack();
            }}
          />

          <Stack alignItems="center" width="100%" pt={6} flex={1}>
            {isLoading && <CircularProgress />}
            {isSuccess && isDefined(assessmentStatus) && isDefined(scheduledFor) && (
              <OrientationScheduledConfirmation
                scheduledFor={scheduledFor}
                showTimezone={showTimezone}
                assessmentId={latestScheduledEvent?.attributes.assessmentId}
                onContinue={() => {
                  logEvent(
                    APP_V2_USER_EVENTS.CLIPBOARD_ORIENTATION_RESCHEDULED_CONFIRMATION_BUTTON_CLICKED,
                    {
                      isSignup,
                    }
                  );

                  // Handle navigation based on if user is on signup or not
                  if (isSignup) {
                    history.push(OnboardingRouterPath.ONBOARDING_INFO1);
                  } else {
                    history.push(ACCOUNTS_BASE_PATH);
                  }
                }}
              />
            )}
          </Stack>
        </Stack>
      </PageWrapper>
    </ThemeProvider>
  );
}
